import React, { useState, useEffect, useRef, useId } from 'react';

/**
 * GaugeChart — Premium animated SVG gauge with threshold color zones and tick labels.
 *
 * Props:
 *   value        – current sensor reading (number)
 *   unit         – display unit string (e.g. '%', '°C')
 *   colorConfig  – { logic, ranges, colors } from the sensor definition
 *   theme        – 'dark' | 'light'
 *   size         – optional gauge width in px (default 140)
 */

// ── Hex look-up for Tailwind class names ────────────────────────────────────
const HEX = {
  'text-red-500': '#ef4444',
  'text-yellow-500': '#eab308',
  'text-emerald-500': '#10b981',
};

// ── Geometry constants ──────────────────────────────────────────────────────
const DEG_TO_RAD = Math.PI / 180;
const ARC_START = -180;          // left end (degrees, 0 = 3-o'clock)
const ARC_END = 0;               // right end
const ARC_SPAN = ARC_END - ARC_START; // 180°

const polarToXY = (cx, cy, r, angleDeg) => ({
  x: cx + r * Math.cos(angleDeg * DEG_TO_RAD),
  y: cy + r * Math.sin(angleDeg * DEG_TO_RAD),
});

/** SVG arc path from startAngle to endAngle (degrees) */
const arcPath = (cx, cy, r, startDeg, endDeg) => {
  const s = polarToXY(cx, cy, r, startDeg);
  const e = polarToXY(cx, cy, r, endDeg);
  const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
};

// ── Build color zone array from colorConfig ─────────────────────────────────
const buildZones = (colorConfig) => {
  if (!colorConfig?.logic || !colorConfig?.ranges) {
    return [{ from: 0, to: 1, color: '#10b981' }];
  }
  const { logic, ranges } = colorConfig;

  if (logic === 'linearLowToHigh') {
    const m = ranges.green;
    return [
      { from: 0, to: ranges.red / m, color: HEX['text-red-500'] },
      { from: ranges.red / m, to: ranges.yellow / m, color: HEX['text-yellow-500'] },
      { from: ranges.yellow / m, to: 1, color: HEX['text-emerald-500'] },
    ];
  }
  if (logic === 'linearHighToLow') {
    const m = ranges.red;
    return [
      { from: 0, to: ranges.green / m, color: HEX['text-emerald-500'] },
      { from: ranges.green / m, to: ranges.yellow / m, color: HEX['text-yellow-500'] },
      { from: ranges.yellow / m, to: 1, color: HEX['text-red-500'] },
    ];
  }
  if (logic === 'bellCurve') {
    const m = ranges.redHigh;
    return [
      { from: 0, to: ranges.redLow / m, color: HEX['text-red-500'] },
      { from: ranges.redLow / m, to: ranges.yellowLow / m, color: HEX['text-yellow-500'] },
      { from: ranges.yellowLow / m, to: ranges.green / m, color: HEX['text-emerald-500'] },
      { from: ranges.green / m, to: ranges.yellowHigh / m, color: HEX['text-yellow-500'] },
      { from: ranges.yellowHigh / m, to: 1, color: HEX['text-red-500'] },
    ];
  }
  return [{ from: 0, to: 1, color: '#10b981' }];
};

// ── Value → [0..1] normalized position ──────────────────────────────────────
const normalizeValue = (value, colorConfig) => {
  if (value == null || isNaN(value)) return 0;
  const v = Number(value);
  if (!colorConfig?.logic || !colorConfig?.ranges) return Math.min(1, Math.max(0, v / 100));
  const { logic, ranges } = colorConfig;
  let max = 100;
  if (logic === 'linearLowToHigh') max = ranges.green;
  else if (logic === 'linearHighToLow') max = ranges.red;
  else if (logic === 'bellCurve') max = ranges.redHigh;
  return Math.min(1, Math.max(0, v / max));
};

// ── Get the color for a given normalized position ───────────────────────────
const getColorForPosition = (norm, zones) => {
  for (const z of zones) { if (norm <= z.to + 0.001) return z.color; }
  return zones[zones.length - 1].color;
};

// ── Extract tick boundary values from colorConfig ───────────────────────────
const getTickValues = (cc) => {
  if (!cc?.logic || !cc?.ranges) return { min: 0, max: 100, boundaries: [] };
  const r = cc.ranges;
  if (cc.logic === 'linearLowToHigh') return { min: 0, max: r.green, boundaries: [r.red, r.yellow] };
  if (cc.logic === 'linearHighToLow') return { min: 0, max: r.red, boundaries: [r.green, r.yellow] };
  if (cc.logic === 'bellCurve') return { min: 0, max: r.redHigh, boundaries: [r.redLow, r.yellowLow, r.green, r.yellowHigh] };
  return { min: 0, max: 100, boundaries: [] };
};

// ── Filter ticks to prevent label overlap (min 18° angular gap) ─────────────
const filterTicks = (tickVals, max) => {
  const MIN_GAP = 18; // minimum degrees between adjacent labels
  if (tickVals.length <= 2) return tickVals;

  const result = [tickVals[0]]; // always keep first (min)
  let lastAngle = ARC_START + (tickVals[0] / max) * ARC_SPAN;

  for (let i = 1; i < tickVals.length - 1; i++) {
    const angle = ARC_START + (tickVals[i] / max) * ARC_SPAN;
    if (angle - lastAngle >= MIN_GAP) {
      result.push(tickVals[i]);
      lastAngle = angle;
    }
  }

  // Always keep last (max)
  result.push(tickVals[tickVals.length - 1]);
  return result;
};

/** Format tick value for display — integers as-is, decimals to 1 place */
const fmtTick = (v) => Number.isInteger(v) ? String(v) : v.toFixed(1);

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const GaugeChart = ({ value, unit, colorConfig, theme, size = 140 }) => {
  const uniqueId = useId();
  const [animatedNorm, setAnimatedNorm] = useState(0);
  const prevValueRef = useRef(null);
  const animFrameRef = useRef(null);

  const targetNorm = normalizeValue(value, colorConfig);
  const zones = buildZones(colorConfig);

  // ── Smooth animation using requestAnimationFrame ────────────────────────
  useEffect(() => {
    const startNorm = prevValueRef.current ?? 0;
    const endNorm = targetNorm;
    const duration = 1000; // ms
    let startTime = null;
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = startNorm + (endNorm - startNorm) * easeOutCubic(progress);
      setAnimatedNorm(current);

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        prevValueRef.current = endNorm;
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, [targetNorm]);

  // ── Layout geometry (with padding for tick labels) ──────────────────────
  const strokeWidth = 14;
  const labelPad = 14;               // space reserved on each side for tick labels
  const svgW = size;                  // 140
  const svgH = size * 0.68;          // ~95
  const cx = svgW / 2;               // 70
  const cy = svgH - 8;               // ~87
  const r = (svgW - 2 * labelPad - strokeWidth) / 2; // ~49

  // ── Needle ──────────────────────────────────────────────────────────────
  const needleAngle = ARC_START + animatedNorm * ARC_SPAN;
  const needleColor = getColorForPosition(animatedNorm, zones);
  const needleLen = r - 4;
  const needleTip = polarToXY(cx, cy, needleLen, needleAngle);
  const needleBaseL = polarToXY(cx, cy, 5, needleAngle + 90);
  const needleBaseR = polarToXY(cx, cy, 5, needleAngle - 90);

  // ── Theme-aware colors ──────────────────────────────────────────────────
  const trackColor = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(27,38,59,0.06)';
  const unitColor = theme === 'dark' ? '#94a3b8' : '#64748b';
  const tickLabelColor = theme === 'dark' ? '#64748b' : '#94a3b8';
  const tickLineColor = theme === 'dark' ? 'rgba(15,23,42,0.6)' : 'rgba(255,255,255,0.8)';

  // ── Tick values for labels ──────────────────────────────────────────────
  const tickInfo = getTickValues(colorConfig);
  const allTickVals = [tickInfo.min, ...tickInfo.boundaries, tickInfo.max];
  const filteredTicks = filterTicks(allTickVals, tickInfo.max);
  const labelRadius = r + strokeWidth / 2 + 8;

  // ── Formatted display value ─────────────────────────────────────────────
  const displayVal = value != null ? Number(value).toFixed(2) : '--';

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${svgW} ${svgH + 28}`}
      className="select-none drop-shadow-sm"
      style={{ overflow: 'visible', maxHeight: '100%' }}
    >
      {/* ── Background track ──────────────────────────────────────────── */}
      <path
        d={arcPath(cx, cy, r, ARC_START, ARC_END)}
        fill="none"
        stroke={trackColor}
        strokeWidth={strokeWidth + 4}
        strokeLinecap="round"
      />

      {/* ── Color zone arcs ───────────────────────────────────────────── */}
      {zones.map((zone, i) => {
        const startDeg = ARC_START + zone.from * ARC_SPAN;
        const endDeg = ARC_START + zone.to * ARC_SPAN;
        if (Math.abs(endDeg - startDeg) < 0.5) return null;
        return (
          <path
            key={`zone-${uniqueId}-${i}`}
            d={arcPath(cx, cy, r, startDeg, endDeg)}
            fill="none"
            stroke={zone.color}
            strokeWidth={strokeWidth}
            strokeLinecap="butt"
            opacity={0.85}
          />
        );
      })}

      {/* ── Rounded caps on arc endpoints ─────────────────────────────── */}
      {(() => {
        const capR = strokeWidth / 2;
        const leftCap = polarToXY(cx, cy, r, ARC_START);
        const rightCap = polarToXY(cx, cy, r, ARC_END);
        return (
          <>
            <circle cx={leftCap.x} cy={leftCap.y} r={capR} fill={zones[0]?.color || '#10b981'} opacity={0.85} />
            <circle cx={rightCap.x} cy={rightCap.y} r={capR} fill={zones[zones.length - 1]?.color || '#ef4444'} opacity={0.85} />
          </>
        );
      })()}

      {/* ── Tick marks at zone boundaries ──────────────────────────────── */}
      {zones.slice(1).map((zone, i) => {
        const angle = ARC_START + zone.from * ARC_SPAN;
        const inner = polarToXY(cx, cy, r - strokeWidth / 2 - 2, angle);
        const outer = polarToXY(cx, cy, r + strokeWidth / 2 + 2, angle);
        return (
          <line
            key={`tick-${uniqueId}-${i}`}
            x1={inner.x} y1={inner.y}
            x2={outer.x} y2={outer.y}
            stroke={tickLineColor}
            strokeWidth={2}
          />
        );
      })}

      {/* ── Numeric tick labels ────────────────────────────────────────── */}
      {filteredTicks.map((val, i) => {
        const norm = tickInfo.max > 0 ? val / tickInfo.max : 0;
        const angle = ARC_START + norm * ARC_SPAN;
        const pos = polarToXY(cx, cy, labelRadius, angle);

        // Text anchor: labels on the left extend right, on the right extend left
        let anchor = 'middle';
        if (angle < -140) anchor = 'start';
        else if (angle > -40) anchor = 'end';

        return (
          <text
            key={`label-${uniqueId}-${i}`}
            x={pos.x}
            y={pos.y}
            textAnchor={anchor}
            dominantBaseline="central"
            fontSize="8"
            fontWeight="600"
            fill={tickLabelColor}
            style={{ fontFamily: 'Inter, sans-serif', userSelect: 'none' }}
          >
            {fmtTick(val)}
          </text>
        );
      })}

      {/* ── Needle (triangle pointer) ─────────────────────────────────── */}
      <polygon
        points={`${needleTip.x},${needleTip.y} ${needleBaseL.x},${needleBaseL.y} ${needleBaseR.x},${needleBaseR.y}`}
        fill={needleColor}
        style={{ filter: `drop-shadow(0 2px 4px ${needleColor}66)` }}
      />

      {/* ── Center pivot dot ──────────────────────────────────────────── */}
      <circle
        cx={cx} cy={cy} r={7}
        fill={needleColor}
        stroke={theme === 'dark' ? '#0f172a' : '#ffffff'}
        strokeWidth={3}
      />
      <circle cx={cx} cy={cy} r={2.5} fill={theme === 'dark' ? '#0f172a' : '#ffffff'} />

      {/* ── Value + unit text ─────────────────────────────────────────── */}
      <text
        x={cx}
        y={cy + 18}
        textAnchor="middle"
        dominantBaseline="hanging"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <tspan
          fill={needleColor}
          fontSize="15"
          fontWeight="800"
          letterSpacing="-0.02em"
        >
          {displayVal}
        </tspan>
        {unit && (
          <tspan
            fill={unitColor}
            fontSize="9"
            fontWeight="600"
            dx="2"
          >
            {` ${unit}`}
          </tspan>
        )}
      </text>
    </svg>
  );
};

export default GaugeChart;
